import { getCloudFunctionUrl } from "../firebase";

const FUNCTION_URL = getCloudFunctionUrl("deleteAccount");

const PENDING_DELETIONS_KEY = 'enrollease.pendingAccountDeletions.v1';

function readPendingDeletions() {
  const serialized = window.localStorage.getItem(PENDING_DELETIONS_KEY);
  if (!serialized) return [];
  const pending = JSON.parse(serialized);
  if (!Array.isArray(pending) || pending.some(item => (
    !item ||
    !['staff', 'student'].includes(item.targetType) ||
    typeof item.documentId !== 'string' ||
    !item.documentId ||
    item.documentId.includes('/')
  ))) {
    throw new Error('Stored account-deletion recovery information is invalid.');
  }
  return pending;
}

function writePendingDeletions(pending) {
  window.localStorage.setItem(PENDING_DELETIONS_KEY, JSON.stringify(pending));
}

function targetKey(targetType, documentId) {
  return `${targetType}:${documentId}`;
}

function savePendingDeletion(targetType, documentId, operationId) {
  const pending = readPendingDeletions();
  const key = targetKey(targetType, documentId);
  const entry = pending.find(item => targetKey(item.targetType, item.documentId) === key);
  if (entry) {
    if (operationId) entry.operationId = operationId;
  } else {
    pending.push({ targetType, documentId, ...(operationId ? { operationId } : {}) });
  }
  writePendingDeletions(pending);
}

function completePendingDeletion(targetType, documentId) {
  const key = targetKey(targetType, documentId);
  writePendingDeletions(
    readPendingDeletions().filter(item => targetKey(item.targetType, item.documentId) !== key)
  );
}

function updatePendingOperationId(targetType, documentId, operationId) {
  if (operationId) savePendingDeletion(targetType, documentId, operationId);
}

export async function requestAccountDeletion({ user, targetType, documentId, operationId }) {
  if (!user) throw new Error('Sign in again before deleting this account.');

  let target = null;
  if (targetType && documentId) {
    target = { targetType, documentId };
    savePendingDeletion(targetType, documentId);
  } else if (operationId) {
    target = readPendingDeletions().find(item => item.operationId === operationId) || null;
  }

  const token = await user.getIdToken();
  const response = await fetch(FUNCTION_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(operationId
      ? { operationId }
      : { targetType, documentId, mode: 'permanent' })
  });
  const result = await response.json().catch(() => null);

  if (response.status === 202) {
    if (
      result?.ok !== false ||
      result?.outcome !== 'recovery_pending' ||
      typeof result.operationId !== 'string' ||
      !result.operationId
    ) {
      throw new Error('The server returned an invalid deletion-recovery response.');
    }
  } else if (
    !response.ok ||
    result?.ok !== true ||
    !['deleted', 'record_only'].includes(result.outcome) ||
    typeof result.operationId !== 'string' ||
    !result.operationId
  ) {
    const error = new Error(result?.error?.message || 'Account deletion failed.');
    error.code = result?.error?.code || 'deletion_failed';
    throw error;
  }

  const deletionResult = result;
  if (target) {
    if (deletionResult.ok === true && ['deleted', 'record_only'].includes(deletionResult.outcome)) {
      completePendingDeletion(target.targetType, target.documentId);
    } else {
      updatePendingOperationId(target.targetType, target.documentId, deletionResult.operationId);
    }
  }
  return deletionResult;
}

export async function recoverPendingAccountDeletions(user) {
  if (!user) return [];
  const pending = readPendingDeletions();
  return Promise.all(pending.map(async item => {
    try {
      const result = await requestAccountDeletion({
        user,
        targetType: item.targetType,
        documentId: item.documentId,
        operationId: item.operationId
      });
      return { ...item, result, complete: result.ok === true };
    } catch (error) {
      if (error.code === 'target_not_found') {
        completePendingDeletion(item.targetType, item.documentId);
        return { ...item, error, complete: false, discarded: true };
      }
      return { ...item, error, complete: false };
    }
  }));
}

export function getPendingAccountDeletions() {
  return readPendingDeletions();
}
